import React, { useEffect, useState } from 'react';
import {
  Layout, Card, Button, Table, Modal, Form, Input, InputNumber, Select,
  Tag, Space, Typography, message, Row, Col, Tooltip, Popconfirm, Tabs,
  Rate, Progress, Alert, Divider, Spin
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined,
  ReloadOutlined, InfoCircleOutlined, BulbOutlined, UserOutlined,
  BookOutlined, StarOutlined, SaveOutlined, CalendarOutlined,
  ApartmentOutlined, SlidersOutlined
} from '@ant-design/icons';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import api from '../api';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const DEFAULT_CATEGORIES = [
  'Core Competencies',
  'Leadership',
  'Functional / Technical',
  'Behavioral',
  'Customer Focus',
  'General'
];

const CATEGORY_COLOR_MAP = {
  'Core Competencies': 'blue',
  'Leadership': 'purple',
  'Functional / Technical': 'cyan',
  'Behavioral': 'magenta',
  'Customer Focus': 'orange',
  'General': 'default',
};

const STANDARD_COMPETENCIES_SEED = [
  { name: 'Communication', category: 'Core Competencies', description: 'Articulates ideas clearly, listens actively, and communicates effectively across teams.', defaultRatingScale: '1-5 Scale', weightage: 15 },
  { name: 'Leadership', category: 'Leadership', description: 'Inspires, motivates, and guides team members toward achieving common organizational goals.', defaultRatingScale: '1-5 Scale', weightage: 15 },
  { name: 'Teamwork & Collaboration', category: 'Behavioral', description: 'Works constructively with colleagues, builds positive working relationships, and supports team success.', defaultRatingScale: '1-5 Scale', weightage: 15 },
  { name: 'Problem Solving', category: 'Functional / Technical', description: 'Analyzes complex problems, identifies root causes, and implements efficient solutions.', defaultRatingScale: '1-5 Scale', weightage: 15 },
  { name: 'Technical Knowledge', category: 'Functional / Technical', description: 'Demonstrates strong subject matter expertise and stays updated with industry practices.', defaultRatingScale: '1-5 Scale', weightage: 15 },
  { name: 'Customer Orientation', category: 'Customer Focus', description: 'Understands customer needs, delivers high quality service, and maintains high satisfaction.', defaultRatingScale: '1-5 Scale', weightage: 10 },
  { name: 'Time Management', category: 'Behavioral', description: 'Prioritizes tasks efficiently, meets deadlines consistently, and manages workload effectively.', defaultRatingScale: '1-5 Scale', weightage: 10 },
  { name: 'Discipline & Accountability', category: 'Core Competencies', description: 'Takes ownership of work, adheres to workplace standards, and maintains punctuality and integrity.', defaultRatingScale: '1-5 Scale', weightage: 5 },
];

export default function PmsCompetencyAssessment() {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');

  // Competency Library State
  const [competencies, setCompetencies] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const [form] = Form.useForm();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState(null);

  // Meta Data
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [staffList, setStaffList] = useState([]);

  // Assessment Evaluation State
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [evaluatorRole, setEvaluatorRole] = useState('Manager');
  const [assessmentRatings, setAssessmentRatings] = useState({}); // { [comp.id]: { rating, comments } }
  const [savingAssessments, setSavingAssessments] = useState(false);

  const handleAddCustomCategory = (e) => {
    if (e) e.preventDefault();
    const trimmed = newCategoryInput.trim();
    if (!trimmed) return;
    if (!categories.includes(trimmed)) {
      setCategories([...categories, trimmed]);
    }
    form.setFieldsValue({ category: trimmed });
    setNewCategoryInput('');
    message.success(`Custom category "${trimmed}" added!`);
  };

  // Load Library & Meta
  const fetchCompetencies = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/pms/competencies');
      if (res.data?.success) {
        const list = res.data.competencies || [];
        setCompetencies(list);
        const fetchedCats = list.map(c => c.category).filter(Boolean);
        setCategories(Array.from(new Set([...DEFAULT_CATEGORIES, ...fetchedCats])));
      } else {
        message.error(res.data?.message || 'Failed to load competencies');
      }
    } catch (e) {
      console.error('Fetch competencies error:', e);
      message.error(e.response?.data?.message || 'Failed to load competencies');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetaData = async () => {
    try {
      // 1. Appraisal Cycles
      const cycleRes = await api.get('/admin/pms/appraisal-cycles');
      if (cycleRes.data?.success) {
        const list = cycleRes.data.cycles || [];
        setCycles(list);
        const active = list.find(c => c.active) || list[0];
        if (active) setSelectedCycleId(active.id);
      }

      // 2. Staff List
      const staffRes = await api.get('/admin/staff');
      const sList = staffRes.data?.staff || staffRes.data?.data || [];
      setStaffList(sList);
      if (sList.length > 0) {
        const first = sList[0];
        const uid = first.userId || first.user_id || first.user?.id || first.id;
        setSelectedStaffId(uid);
      }

      // 3. Departments & Designations
      const bfResp = await api.get('/admin/business-functions');
      const bfList = bfResp?.data?.data || [];

      const deptFn = bfList.find((f) => String(f.name || '').toLowerCase() === 'department');
      const deptValues = (deptFn?.values || []).map(v => v.value).filter(Boolean);

      const desigFn = bfList.find((f) => ['designation', 'role', 'title'].includes(String(f.name || '').toLowerCase()));
      const desigValues = (desigFn?.values || []).map(v => v.value).filter(Boolean);

      let staffDepts = [];
      let staffDesigs = [];
      sList.forEach(s => {
        const profile = s.profile || s;
        if (profile.department) staffDepts.push(profile.department);
        if (profile.designation) staffDesigs.push(profile.designation);
        if (profile.role) staffDesigs.push(profile.role);
      });

      setDepartments(Array.from(new Set([...deptValues, ...staffDepts])).filter(Boolean));
      setDesignations(Array.from(new Set([...desigValues, ...staffDesigs])).filter(Boolean));
    } catch (e) {
      console.error('Fetch meta data error:', e);
    }
  };

  useEffect(() => {
    fetchCompetencies();
    fetchMetaData();
  }, []);

  // Fetch Existing Assessments for Selected Cycle + Staff
  const fetchAssessmentsForStaff = async () => {
    if (!selectedStaffId) return;
    try {
      const queryParams = {
        employeeId: selectedStaffId,
        evaluatorRole,
      };
      if (selectedCycleId) queryParams.cycleId = selectedCycleId;

      const res = await api.get('/admin/pms/competency-assessments', { params: queryParams });
      if (res.data?.success) {
        const list = res.data.assessments || [];
        const stateMap = {};
        list.forEach(item => {
          stateMap[item.competencyId] = {
            rating: item.rating,
            comments: item.comments || '',
          };
        });
        setAssessmentRatings(stateMap);
      }
    } catch (e) {
      console.error('Fetch staff assessment error:', e);
    }
  };

  useEffect(() => {
    if (selectedStaffId && activeTab === '2') {
      fetchAssessmentsForStaff();
    }
  }, [selectedStaffId, selectedCycleId, evaluatorRole, activeTab]);

  // Handle Save / Edit Competency
  const handleAddEdit = (record = null) => {
    setEditingComp(record);
    if (record) {
      form.setFieldsValue({
        name: record.name,
        category: record.category || 'Core Competencies',
        description: record.description,
        department: record.department,
        designation: record.designation,
        defaultRatingScale: record.defaultRatingScale || '1-5 Scale',
        weightage: record.weightage || 0,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        category: 'Core Competencies',
        defaultRatingScale: '1-5 Scale',
        weightage: 10,
      });
    }
    setIsModalOpen(true);
  };

  const handleSaveCompetency = async () => {
    try {
      const values = await form.validateFields();
      if (editingComp) {
        const res = await api.put(`/admin/pms/competencies/${editingComp.id}`, values);
        if (res.data?.success) {
          message.success('Competency updated successfully');
          setIsModalOpen(false);
          fetchCompetencies();
        } else {
          message.error(res.data?.message || 'Failed to update competency');
        }
      } else {
        const res = await api.post('/admin/pms/competencies', values);
        if (res.data?.success) {
          message.success('Competency added successfully');
          setIsModalOpen(false);
          fetchCompetencies();
        } else {
          message.error(res.data?.message || 'Failed to create competency');
        }
      }
    } catch (e) {
      console.error('Save competency error:', e);
      if (e.response?.data?.message) {
        message.error(e.response.data.message);
      }
    }
  };

  const handleDeleteCompetency = async (id) => {
    try {
      const res = await api.delete(`/admin/pms/competencies/${id}`);
      if (res.data?.success) {
        message.success('Competency deleted');
        fetchCompetencies();
      } else {
        message.error(res.data?.message || 'Failed to delete');
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to delete');
    }
  };

  const handleSeedStandardCompetencies = async () => {
    try {
      setLoading(true);
      let addedCount = 0;
      for (const item of STANDARD_COMPETENCIES_SEED) {
        // Check if already exists
        const exists = competencies.some(c => c.name.toLowerCase() === item.name.toLowerCase());
        if (!exists) {
          await api.post('/admin/pms/competencies', item);
          addedCount++;
        }
      }
      if (addedCount > 0) {
        message.success(`Added ${addedCount} standard competencies to your library!`);
        fetchCompetencies();
      } else {
        message.info('All standard competencies already exist in your library.');
      }
    } catch (e) {
      console.error('Seed error:', e);
      message.error('Failed to seed standard competencies');
    } finally {
      setLoading(false);
    }
  };

  // Assessment Rating Handler
  const handleRatingChange = (competencyId, rating) => {
    setAssessmentRatings(prev => ({
      ...prev,
      [competencyId]: {
        ...prev[competencyId],
        rating,
      }
    }));
  };

  const handleCommentChange = (competencyId, comments) => {
    setAssessmentRatings(prev => ({
      ...prev,
      [competencyId]: {
        ...prev[competencyId],
        comments,
      }
    }));
  };

  const handleSaveAllAssessments = async () => {
    if (!selectedStaffId) {
      return message.warning('Please select an employee to assess');
    }
    const assessmentArray = [];
    Object.keys(assessmentRatings).forEach(compId => {
      const item = assessmentRatings[compId];
      if (item && item.rating > 0) {
        assessmentArray.push({
          competencyId: Number(compId),
          rating: item.rating,
          maxRating: 5,
          comments: item.comments || '',
        });
      }
    });

    if (assessmentArray.length === 0) {
      return message.warning('Please rate at least one competency before saving.');
    }

    try {
      setSavingAssessments(true);
      const payload = {
        cycleId: selectedCycleId,
        employeeId: selectedStaffId,
        evaluatorRole,
        assessments: assessmentArray,
      };

      const res = await api.post('/admin/pms/competency-assessments/bulk', payload);
      if (res.data?.success) {
        message.success('Competency assessment ratings saved successfully!');
        fetchAssessmentsForStaff();
      } else {
        message.error(res.data?.message || 'Failed to save assessments');
      }
    } catch (e) {
      console.error('Save assessments error:', e);
      message.error(e.response?.data?.message || 'Failed to save assessments');
    } finally {
      setSavingAssessments(false);
    }
  };

  // Filtered Competencies for Library Table
  const filteredCompetencies = competencies.filter(c => {
    const matchesSearch = !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()) || (c.description && c.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = !filterCategory || c.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Calculate Weighted Score Summary
  const calculateOverallScore = () => {
    let totalWeight = 0;
    let weightedScoreSum = 0;
    let ratedCount = 0;

    competencies.forEach(comp => {
      const userRating = assessmentRatings[comp.id]?.rating;
      if (userRating > 0) {
        const weight = comp.weightage || 10;
        totalWeight += weight;
        weightedScoreSum += (userRating / 5) * weight;
        ratedCount++;
      }
    });

    const overallPercentage = totalWeight > 0 ? (weightedScoreSum / totalWeight) * 100 : 0;
    const avgRating = ratedCount > 0 ? Object.values(assessmentRatings).reduce((acc, curr) => acc + (curr.rating || 0), 0) / ratedCount : 0;

    return {
      totalWeight,
      weightedScoreSum,
      ratedCount,
      overallPercentage: overallPercentage.toFixed(1),
      avgRating: avgRating.toFixed(2),
    };
  };

  const summary = calculateOverallScore();

  const columns = [
    {
      title: 'Competency Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: 15 }}>{text}</Text>
          {record.description && (
            <div style={{ color: '#666', fontSize: 13, marginTop: 2 }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 180,
      render: (cat) => (
        <Tag color={CATEGORY_COLOR_MAP[cat] || 'default'} key={cat}>
          {cat || 'General'}
        </Tag>
      ),
    },
    {
      title: 'Department / Role',
      key: 'scope',
      width: 200,
      render: (_, record) => (
        <div>
          <div><Text type="secondary">Dept:</Text> {record.department || 'All'}</div>
          <div><Text type="secondary">Desig:</Text> {record.designation || 'All'}</div>
        </div>
      ),
    },
    {
      title: 'Scale & Weight',
      key: 'weightage',
      width: 150,
      render: (_, record) => (
        <div>
          <div><Tag color="blue">{record.defaultRatingScale || '1-5 Scale'}</Tag></div>
          <div style={{ marginTop: 4 }}>
            <Text type="secondary">Weightage:</Text> <strong>{record.weightage || 0}%</strong>
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Competency">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: '#1890ff' }} />}
              onClick={() => handleAddEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this competency?"
            description="Are you sure you want to delete this competency from your library?"
            onConfirm={() => handleDeleteCompetency(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, minHeight: '100vh', transition: 'margin-left 0.2s', background: '#f0f2f5' }}>
        <MainHeader collapsed={collapsed} setCollapsed={setCollapsed} title="Competency Assessment" />
        <Content style={{ margin: '24px 24px', overflow: 'initial' }}>
          
          {/* Header Card */}
          <Card
            style={{ marginBottom: 20, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
            bodyStyle={{ padding: '20px 24px' }}
          >
            <Row justify="space-between" align="middle" gutter={[16, 16]}>
              <Col xs={24} md={16}>
                <Space align="center" size={12}>
                  <BookOutlined style={{ fontSize: 28, color: '#1890ff' }} />
                  <div>
                    <Title level={3} style={{ margin: 0 }}>Competency Assessment</Title>
                    <Text type="secondary">
                      Evaluate employee behavioral, leadership, and technical competencies independently of quantitative KRA/KPI targets.
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                <Space wrap>
                  <Button
                    type="default"
                    icon={<ReloadOutlined />}
                    onClick={() => { fetchCompetencies(); fetchMetaData(); }}
                  >
                    Refresh
                  </Button>
                  {activeTab === '1' && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => handleAddEdit()}
                    >
                      Add Competency
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Main Navigation Tabs */}
          <Card style={{ borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              type="card"
              items={[
                {
                  key: '1',
                  label: (
                    <span>
                      <BookOutlined /> Competency Library ({competencies.length})
                    </span>
                  ),
                  children: (
                    <div>
                      {/* Filter Bar */}
                      <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
                        <Col xs={24} sm={10} md={8}>
                          <Input.Search
                            placeholder="Search competency name or description..."
                            allowClear
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </Col>
                        <Col xs={24} sm={8} md={6}>
                          <Select
                            placeholder="Filter by Category"
                            allowClear
                            style={{ width: '100%' }}
                            value={filterCategory}
                            onChange={setFilterCategory}
                          >
                            {categories.map(cat => (
                              <Option key={cat} value={cat}>{cat}</Option>
                            ))}
                          </Select>
                        </Col>
                      </Row>

                      <Table
                        columns={columns}
                        dataSource={filteredCompetencies}
                        rowKey="id"
                        loading={loading}
                        pagination={{ pageSize: 10, showSizeChanger: true }}
                      />
                    </div>
                  ),
                },
                {
                  key: '2',
                  label: (
                    <span>
                      <StarOutlined /> Evaluate Staff Competencies
                    </span>
                  ),
                  children: (
                    <div>
                      {/* Staff & Cycle Selection Bar */}
                      <Card
                        type="inner"
                        style={{ background: '#fafafa', marginBottom: 20 }}
                        bodyStyle={{ padding: 16 }}
                      >
                        <Row gutter={[16, 16]} align="middle">
                          <Col xs={24} sm={8} md={6}>
                            <Text strong><CalendarOutlined style={{ marginRight: 6 }} /> Appraisal Cycle:</Text>
                            <Select
                              style={{ width: '100%', marginTop: 4 }}
                              placeholder="Select Cycle"
                              value={selectedCycleId}
                              onChange={setSelectedCycleId}
                            >
                              {cycles.map(c => (
                                <Option key={c.id} value={c.id}>
                                  {c.cycleName} ({c.financialYear}) {c.active ? ' [Active]' : ''}
                                </Option>
                              ))}
                            </Select>
                          </Col>

                          <Col xs={24} sm={8} md={8}>
                            <Text strong><UserOutlined style={{ marginRight: 6 }} /> Employee:</Text>
                            <Select
                              showSearch
                              style={{ width: '100%', marginTop: 4 }}
                              placeholder="Select Employee to Assess"
                              value={selectedStaffId}
                              onChange={setSelectedStaffId}
                              optionFilterProp="children"
                            >
                              {staffList.map(s => {
                                const profile = s.profile || s;
                                const userId = s.userId || s.user_id || s.user?.id || s.id;
                                const name = profile.name || s.username || `User #${userId}`;
                                const desig = profile.designation ? ` (${profile.designation})` : '';
                                return (
                                  <Option key={userId} value={userId}>
                                    {name} {desig}
                                  </Option>
                                );
                              })}
                            </Select>
                          </Col>

                          <Col xs={24} sm={8} md={4}>
                            <Text strong>Evaluator Role:</Text>
                            <Select
                              style={{ width: '100%', marginTop: 4 }}
                              value={evaluatorRole}
                              onChange={setEvaluatorRole}
                            >
                              <Option value="Manager">Manager</Option>
                              <Option value="Self">Self</Option>
                              <Option value="Peer">Peer</Option>
                              <Option value="Subordinate">Subordinate</Option>
                            </Select>
                          </Col>

                          <Col xs={24} sm={24} md={6} style={{ textAlign: 'right', alignSelf: 'flex-end' }}>
                            <Button
                              type="primary"
                              icon={<SaveOutlined />}
                              size="large"
                              loading={savingAssessments}
                              onClick={handleSaveAllAssessments}
                            >
                              Save Assessment
                            </Button>
                          </Col>
                        </Row>
                      </Card>

                      {/* Summary Stat Banner */}
                      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                        <Col xs={24} sm={8}>
                          <Card size="small" style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}>
                            <Text type="secondary">Evaluated Competencies</Text>
                            <Title level={3} style={{ margin: '4px 0 0', color: '#1890ff' }}>
                              {summary.ratedCount} / {competencies.length}
                            </Title>
                          </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                            <Text type="secondary">Average Score (out of 5)</Text>
                            <Title level={3} style={{ margin: '4px 0 0', color: '#52c41a' }}>
                              {summary.avgRating} <span style={{ fontSize: 14, color: '#8c8c8c' }}>/ 5.0</span>
                            </Title>
                          </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Card size="small" style={{ background: '#fff7e6', borderColor: '#ffd591' }}>
                            <Text type="secondary">Overall Weighted Rating</Text>
                            <div style={{ marginTop: 4 }}>
                              <Progress
                                percent={Number(summary.overallPercentage)}
                                status="active"
                                strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                              />
                            </div>
                          </Card>
                        </Col>
                      </Row>

                      {/* Competency Evaluation Form Cards */}
                      {competencies.length === 0 ? (
                        <Alert
                          message="No Competencies Found"
                          description="Please add competencies in the Competency Library tab first."
                          type="warning"
                          showIcon
                        />
                      ) : (
                        <Row gutter={[16, 16]}>
                          {competencies.map((comp, idx) => {
                            const curAssessment = assessmentRatings[comp.id] || {};
                            return (
                              <Col xs={24} md={12} key={comp.id}>
                                <Card
                                  hoverable
                                  style={{
                                    height: '100%',
                                    borderRadius: 8,
                                    borderColor: curAssessment.rating ? '#b7eb8f' : '#f0f0f0',
                                    borderLeft: `4px solid ${CATEGORY_COLOR_MAP[comp.category] ? '#1890ff' : '#d9d9d9'}`,
                                  }}
                                  bodyStyle={{ padding: 18 }}
                                >
                                  <Row justify="space-between" align="top">
                                    <Col span={18}>
                                      <Text type="secondary" style={{ fontSize: 12 }}>
                                        #{idx + 1} &bull; {comp.category || 'General'}
                                      </Text>
                                      <Title level={5} style={{ marginTop: 2, marginBottom: 4 }}>
                                        {comp.name}
                                      </Title>
                                    </Col>
                                    <Col span={6} style={{ textAlign: 'right' }}>
                                      <Tag color="blue">{comp.weightage || 10}% Weight</Tag>
                                    </Col>
                                  </Row>

                                  {comp.description && (
                                    <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
                                      {comp.description}
                                    </Paragraph>
                                  )}

                                  <Divider style={{ margin: '12px 0' }} />

                                  <Row align="middle" justify="space-between">
                                    <Col span={10}>
                                      <Text strong>Rating (1 to 5):</Text>
                                    </Col>
                                    <Col span={14} style={{ textAlign: 'right' }}>
                                      <Rate
                                        allowHalf
                                        value={curAssessment.rating || 0}
                                        onChange={(val) => handleRatingChange(comp.id, val)}
                                      />
                                      <span style={{ marginLeft: 8, fontWeight: 'bold', color: '#1890ff' }}>
                                        {curAssessment.rating || 0} / 5
                                      </span>
                                    </Col>
                                  </Row>

                                  <div style={{ marginTop: 12 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Comments / Observations:</Text>
                                    <TextArea
                                      rows={2}
                                      placeholder="Provide specific notes, feedback or examples..."
                                      style={{ marginTop: 4, borderRadius: 4 }}
                                      value={curAssessment.comments || ''}
                                      onChange={(e) => handleCommentChange(comp.id, e.target.value)}
                                    />
                                  </div>
                                </Card>
                              </Col>
                            );
                          })}
                        </Row>
                      )}

                      {competencies.length > 0 && (
                        <div style={{ textAlign: 'center', marginTop: 24, marginBottom: 16 }}>
                          <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            size="large"
                            loading={savingAssessments}
                            onClick={handleSaveAllAssessments}
                            style={{ minWidth: 200 }}
                          >
                            Save Competency Assessment
                          </Button>
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>

          {/* Add / Edit Competency Modal - Upgraded to 880px Spacious Card Design */}
          <Modal
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: 4, height: 22, background: '#1677ff', borderRadius: 2 }} />
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
                  {editingComp ? 'Edit Competency Template' : 'Create New Competency'}
                </span>
              </div>
            }
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            footer={null}
            width={880}
            destroyOnClose
            style={{ top: 20 }}
          >
            <Form form={form} layout="vertical" onFinish={handleSaveCompetency} style={{ marginTop: '16px' }}>
              
              {/* Card 1: Competency Profile & Hierarchy */}
              <Card
                type="inner"
                title={
                  <Space>
                    <BulbOutlined style={{ color: '#1677ff' }} />
                    <span style={{ fontWeight: 600, color: '#334155' }}>Competency Definition & Evaluation Type</span>
                  </Space>
                }
                style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
                bodyStyle={{ padding: '16px 20px' }}
              >
                <Row gutter={16}>
                  <Col span={14}>
                    <Form.Item
                      name="name"
                      label={<strong>Competency Name</strong>}
                      rules={[{ required: true, message: 'Please enter competency name' }]}
                    >
                      <Input placeholder="e.g. Strategic Thinking, Cross-Functional Leadership, Problem Solving" />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      name="category"
                      label={<strong>Competency Category</strong>}
                      rules={[{ required: true, message: 'Select or add category' }]}
                    >
                      <Select
                        showSearch
                        placeholder="Select or add custom category"
                        optionFilterProp="children"
                        dropdownRender={(menu) => (
                          <>
                            {menu}
                            <Divider style={{ margin: '8px 0' }} />
                            <Space style={{ padding: '0 8px 4px', display: 'flex' }}>
                              <Input
                                placeholder="Type custom category..."
                                value={newCategoryInput}
                                onChange={(e) => setNewCategoryInput(e.target.value)}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter') {
                                    handleAddCustomCategory(e);
                                  }
                                }}
                              />
                              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCustomCategory}>
                                Add
                              </Button>
                            </Space>
                          </>
                        )}
                      >
                        {categories.map(cat => (
                          <Option key={cat} value={cat}>{cat}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="weightage"
                      label={<strong>Evaluation Weightage (%)</strong>}
                      rules={[{ required: true, message: 'Enter weightage' }]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber min={0} max={100} style={{ width: '100%' }} suffix="%" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="defaultRatingScale"
                      label={<strong>Rating Scale Scheme</strong>}
                      style={{ marginBottom: 0 }}
                    >
                      <Select>
                        <Option value="1-5 Scale">⭐ 1-5 Star Scale (Standard)</Option>
                        <Option value="1-10 Scale">🔢 1-10 Scale (Detailed)</Option>
                        <Option value="Percentage">📊 Percentage (0-100%)</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Card 2: Behavioral Indicators & Guidelines */}
              <Card
                type="inner"
                title={
                  <Space>
                    <BookOutlined style={{ color: '#52c41a' }} />
                    <span style={{ fontWeight: 600, color: '#334155' }}>Behavioral Indicators & Evaluation Rubric</span>
                  </Space>
                }
                style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
                bodyStyle={{ padding: '16px 20px' }}
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item name="description" label={<strong>Description / Observable Behaviors</strong>} style={{ marginBottom: 0 }}>
                      <TextArea rows={3} placeholder="Describe the observable actions, behaviors, or performance expectations for this competency..." />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Card 3: Organizational Applicability Scope */}
              <Card
                type="inner"
                title={
                  <Space>
                    <ApartmentOutlined style={{ color: '#722ed1' }} />
                    <span style={{ fontWeight: 600, color: '#334155' }}>Organizational Applicability (Optional)</span>
                  </Space>
                }
                style={{ marginBottom: 16, borderRadius: 10, borderColor: '#e2e8f0', background: '#fafbfc' }}
                bodyStyle={{ padding: '16px 20px' }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="department" label={<strong>Target Department</strong>} style={{ marginBottom: 0 }}>
                      <Select placeholder="All Departments (Organization-Wide)" allowClear showSearch>
                        {departments.map(d => (
                          <Option key={d} value={d}>{d}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="designation" label={<strong>Target Designation</strong>} style={{ marginBottom: 0 }}>
                      <Select placeholder="All Designations (All Levels)" allowClear showSearch>
                        {designations.map(d => (
                          <Option key={d} value={d}>{d}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                <Button size="large" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                <Button size="large" type="primary" htmlType="submit">
                  {editingComp ? 'Update Competency' : 'Create Competency'}
                </Button>
              </div>
            </Form>
          </Modal>

        </Content>
      </Layout>
    </Layout>
  );
}
